import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  useSyncExternalStore
} from 'react'
import type { PropsWithChildren } from 'react'

import type { ThemeMode } from './theme'

export type ThemePreference = ThemeMode | 'system'

type ThemeModeContextValue = {
  mode: ThemeMode
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

// Same key and default as ebs-platform-web (`@shared/theme` ThemeModeContext).
// `public/theme-boot.js` reads it too, to paint the right mode before React
// mounts — keep the two in step. Light stays the default: a stale value from
// the retired `skalx:themeMode` key is never read.
const STORAGE_KEY = 'skalx-theme-mode'
const DARK_QUERY = '(prefers-color-scheme: dark)'
const PREFERENCES: ThemePreference[] = ['light', 'dark', 'system']

const isPreference = (value: unknown): value is ThemePreference =>
  PREFERENCES.includes(value as ThemePreference)

const readStoredPreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)

    return isPreference(stored) ? stored : 'light'
  } catch {
    return 'light'
  }
}

const storePreference = (preference: ThemePreference) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, preference)
  } catch {
    return
  }
}

const subscribeToSystemScheme = (onChange: () => void) => {
  const query = window.matchMedia?.(DARK_QUERY)

  query?.addEventListener('change', onChange)

  return () => query?.removeEventListener('change', onChange)
}

const systemPrefersDark = () => Boolean(window.matchMedia?.(DARK_QUERY).matches)

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  preference: 'light',
  setPreference: () => undefined
})

/**
 * Light / Dark / System, chosen from the account menu and persisted per
 * browser. Everything dark outside AntD is gated on `:root[data-theme='dark']`
 * (see `tokens.css` and `global.css`), which is written from here.
 */
export const ThemeModeProvider = ({ children }: PropsWithChildren) => {
  const [preference, setPreferenceState] =
    useState<ThemePreference>(readStoredPreference)

  const prefersDark = useSyncExternalStore(
    subscribeToSystemScheme,
    systemPrefersDark,
    () => false
  )

  const mode: ThemeMode =
    preference === 'system' ? (prefersDark ? 'dark' : 'light') : preference

  useLayoutEffect(() => {
    const root = document.documentElement

    root.dataset.theme = mode
    root.style.colorScheme = mode
  }, [mode])

  const setPreference = (next: ThemePreference) => {
    storePreference(next)
    setPreferenceState(next)
  }

  return (
    <ThemeModeContext value={{ mode, preference, setPreference }}>
      {children}
    </ThemeModeContext>
  )
}

export const useThemeMode = () => useContext(ThemeModeContext)
